import type { BladeStatusEvent } from '@avisoma/shared';
import { SourcePageCell } from './SourcePageCell';
import { Td, Th, TruncatedTd } from './cells';
import { FindingMarkerCell, FindingMarkerHeader, type RowFindings } from './FindingMarkerCell';
import {
  extractionRowClass,
  extractionTableClass,
  extractionTbodyClass,
  extractionTheadClass,
} from './tableClasses';

type BladeRow = BladeStatusEvent & { pageId?: string };

/**
 * Dense grid for `bladeStatusEvents` (HPT rotor blades, fan blades and turbine
 * nozzle assemblies read from TRAX P/N Detail Print pages, DR-4620, and the
 * OFF / ON rows of blade / nozzle configuration sheets, DR-5467). These are
 * tracked parts, not life-limited parts, so the grid shows identity, position,
 * movement, status and clocks only — no limit or remaining columns. Hours are
 * decimal, cycles integer. A P/N Detail Print row has no position, movement,
 * status or work order; those cells render blank.
 * Used on both the entity view (consolidated extraction, with a source-page
 * column) and the page view (raw per-page rows, no page column — pass no
 * `pageIdToPosition`).
 */
export function BladeTable({
  rows,
  pageIdToPosition,
  onInspect,
  rowFindings,
}: {
  rows: BladeRow[];
  pageIdToPosition?: Map<string, number>;
  onInspect: (row: BladeRow, index: number) => void;
  rowFindings?: RowFindings;
}) {
  return (
    <table className={extractionTableClass}>
      <thead className={extractionTheadClass}>
        <tr>
          {rowFindings && <FindingMarkerHeader />}
          {pageIdToPosition && <Th>Page</Th>}
          <Th>#</Th>
          <Th>Pos</Th>
          <Th>Description</Th>
          <Th>Off/On</Th>
          <Th>P/N</Th>
          <Th>S/N</Th>
          <Th>TSN</Th>
          <Th>CSN</Th>
          <Th>TSO</Th>
          <Th>CSO</Th>
          <Th>TSR</Th>
          <Th>CSR</Th>
          <Th>Status</Th>
          <Th>W/O</Th>
        </tr>
      </thead>
      <tbody className={extractionTbodyClass}>
        {rows.map((r, i) => (
          <tr
            key={`${r.pageId ?? 'page'}-${r.rowIndex}-${i}`}
            onClick={() => onInspect(r, i)}
            className={extractionRowClass}
          >
            {rowFindings && <FindingMarkerCell findings={rowFindings.get(i)} />}
            {pageIdToPosition && (
              <Td>
                <SourcePageCell pageId={r.pageId} pageIdToPosition={pageIdToPosition} />
              </Td>
            )}
            <Td className="text-gray-500 dark:text-zinc-400">{r.rowIndex}</Td>
            <Td className="font-mono">{r.position ?? ''}</Td>
            <TruncatedTd value={r.description} maxWidth="max-w-[280px]" />
            <Td className="uppercase">{r.movement ?? ''}</Td>
            <Td className="font-mono">{r.partNumber ?? '—'}</Td>
            <Td className="font-mono">{r.serialNumber ?? '—'}</Td>
            <Td>{r.tsn ?? '—'}</Td>
            <Td>{r.csn ?? '—'}</Td>
            <Td>{r.tso ?? '—'}</Td>
            <Td>{r.cso ?? '—'}</Td>
            <Td>{r.tsr ?? '—'}</Td>
            <Td>{r.csr ?? '—'}</Td>
            <TruncatedTd value={r.status} maxWidth="max-w-[160px]" />
            <Td className="font-mono">{r.workOrder ?? ''}</Td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
